<template>
  <div class="w-full mx-auto">
    <form @submit.prevent="handleRegister" class="space-y-4 sm:space-y-6">
      <div class="text-center mb-6">
        <h2 class="text-2xl sm:text-3xl font-bold text-gray-900 font-['Raleway']">Create your account</h2>
        <p class="mt-3 text-sm text-gray-600">
          Already have an account?
          <button 
            type="button"
            @click="$emit('switch-mode', 'login')"
            class="font-medium text-[#4ECF84] hover:text-[#4A9B7A] font-['Raleway']"
          >
            Sign in here
          </button>
        </p>
      </div>

      <!-- Error Message -->
      <div v-if="authStore.error" class="rounded-md bg-red-50 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">{{ authStore.error }}</h3>
          </div>
        </div>
      </div>

      <!-- Name Fields -->
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label for="firstName" class="block text-sm font-medium text-gray-700 mb-2 font-['Raleway']">
            First name
          </label>
          <div>
            <input
              id="firstName"
              v-model="form.firstName"
              name="firstName"
              type="text"
              autocomplete="given-name"
              required
              :class="[
                'appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#4ECF84] focus:border-[#4ECF84] text-base font-[\'Raleway\']',
                errors.firstName ? 'border-red-300' : 'border-gray-300'
              ]"
              placeholder="First name"
            />
            <p v-if="errors.firstName" class="mt-2 text-sm text-red-600">{{ errors.firstName }}</p>
          </div>
        </div>

        <div>
          <label for="lastName" class="block text-sm font-medium text-gray-700 mb-2 font-['Raleway']">
            Last name
          </label>
          <div>
            <input
              id="lastName"
              v-model="form.lastName"
              name="lastName"
              type="text"
              autocomplete="family-name"
              required
              :class="[
                'appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#4ECF84] focus:border-[#4ECF84] text-base font-[\'Raleway\']',
                errors.lastName ? 'border-red-300' : 'border-gray-300'
              ]"
              placeholder="Last name"
            />
            <p v-if="errors.lastName" class="mt-2 text-sm text-red-600">{{ errors.lastName }}</p>
          </div>
        </div>
      </div>

      <!-- Username Field -->
      <div>
        <label for="username" class="block text-sm font-medium text-gray-700 mb-2 font-['Raleway']">
          Username
        </label>
        <div>
          <input
            id="username"
            v-model="form.username"
            name="username"
            type="text"
            autocomplete="username"
            required
            :class="[
              'appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#4ECF84] focus:border-[#4ECF84] text-base font-[\'Raleway\']',
              errors.username ? 'border-red-300' : 'border-gray-300'
            ]"
            placeholder="Choose a username"
          />
          <p v-if="errors.username" class="mt-2 text-sm text-red-600">{{ errors.username }}</p>
        </div>
      </div>

      <!-- Email Field -->
      <div>
        <label for="email" class="block text-sm font-medium text-gray-700 mb-2 font-['Raleway']">
          Email address
        </label>
        <div>
          <input
            id="email"
            v-model="form.email"
            name="email"
            type="email"
            autocomplete="email"
            required
            :class="[
              'appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#4ECF84] focus:border-[#4ECF84] text-base font-[\'Raleway\']',
              errors.email ? 'border-red-300' : 'border-gray-300'
            ]"
            placeholder="Enter your email"
          />
          <p v-if="errors.email" class="mt-2 text-sm text-red-600">{{ errors.email }}</p>
        </div>
      </div>

      <!-- Password Field -->
      <div>
        <label for="password" class="block text-sm font-medium text-gray-700 mb-2 font-['Raleway']">
          Password
        </label>
        <div class="relative">
          <input
            id="password"
            v-model="form.password"
            name="password"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="new-password"
            required
            :class="[
              'appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#4ECF84] focus:border-[#4ECF84] text-base pr-12 font-[\'Raleway\']',
              errors.password ? 'border-red-300' : 'border-gray-300'
            ]"
            placeholder="Create a password"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon 
              :name="showPassword ? 'eye-off' : 'eye'" 
              size="sm"
            />
          </button>
        </div>
        <!-- Password strength indicator -->
        <div class="mt-2">
          <div class="flex space-x-1">
            <div :class="passwordStrength >= 1 ? 'bg-red-500' : 'bg-gray-200'" class="h-1 w-1/4 rounded"></div>
            <div :class="passwordStrength >= 2 ? 'bg-yellow-500' : 'bg-gray-200'" class="h-1 w-1/4 rounded"></div>
            <div :class="passwordStrength >= 3 ? 'bg-blue-500' : 'bg-gray-200'" class="h-1 w-1/4 rounded"></div>
            <div :class="passwordStrength >= 4 ? 'bg-[#4ECF84]' : 'bg-gray-200'" class="h-1 w-1/4 rounded"></div>
          </div>
          <p class="mt-1 text-xs text-gray-600">
            {{ passwordStrengthText }}
          </p>
        </div>
        <p v-if="errors.password" class="mt-2 text-sm text-red-600">{{ errors.password }}</p>
      </div>

      <!-- Confirm Password Field -->
      <div>
        <label for="confirmPassword" class="block text-sm font-medium text-gray-700">
          Confirm password
        </label>
        <div class="mt-1 relative">
          <input
            id="confirmPassword"
            v-model="form.confirmPassword"
            name="confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            autocomplete="new-password"
            required
            :class="[
              'appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-10',
              errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
            ]"
            placeholder="Confirm your password"
          />
          <button
            type="button"
            @click="showConfirmPassword = !showConfirmPassword"
            class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon 
              :name="showConfirmPassword ? 'eye-off' : 'eye'" 
              size="sm"
            />
          </button>
          <p v-if="errors.confirmPassword" class="mt-2 text-sm text-red-600">{{ errors.confirmPassword }}</p>
        </div>
      </div>

      <!-- Terms and Conditions -->
      <div class="flex items-center">
        <input
          id="agreeToTerms"
          v-model="form.agreeToTerms"
          name="agreeToTerms"
          type="checkbox"
          required
          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label for="agreeToTerms" class="ml-2 block text-sm text-gray-900">
          I agree to the
          <a href="#" class="text-blue-600 hover:text-blue-500">Terms and Conditions</a>
          and
          <a href="#" class="text-blue-600 hover:text-blue-500">Privacy Policy</a>
        </label>
      </div>
      <p v-if="errors.agreeToTerms" class="text-sm text-red-600">{{ errors.agreeToTerms }}</p>

      <!-- Submit Button -->
      <div class="pt-2">
        <button
          type="submit"
          :disabled="authStore.isLoading || !isFormValid"
          class="group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-medium rounded-lg text-white bg-[#4ECF84] hover:bg-[#4A9B7A] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4ECF84] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-['Raleway']"
        >
          <span v-if="authStore.isLoading" class="absolute left-0 inset-y-0 flex items-center pl-4">
            <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          {{ authStore.isLoading ? 'Creating account...' : 'Create account' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/store/auth'
import { toastManager } from '@/composables/useToasts.js'
import Icon from '@/components/Icon.vue'

const emit = defineEmits(['switch-mode', 'register-success'])
const authStore = useAuthStore()

// Form state
const form = ref({
  firstName: '',
  lastName: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  agreeToTerms: false
})

const showPassword = ref(false)
const showConfirmPassword = ref(false)
const errors = ref({})

// Computed
const passwordStrength = computed(() => {
  const password = form.value.password
  let strength = 0
  
  if (password.length >= 8) strength++
  if (/[A-Z]/.test(password)) strength++
  if (/[0-9]/.test(password)) strength++
  if (/[^A-Za-z0-9]/.test(password)) strength++
  
  return strength
})

const passwordStrengthText = computed(() => {
  const strength = passwordStrength.value
  if (strength === 0) return 'Enter a password'
  if (strength === 1) return 'Weak password'
  if (strength === 2) return 'Fair password'
  if (strength === 3) return 'Good password'
  return 'Strong password'
})

const isFormValid = computed(() => {
  return form.value.firstName &&
         form.value.lastName &&
         form.value.username &&
         form.value.email &&
         form.value.password &&
         form.value.confirmPassword &&
         form.value.agreeToTerms &&
         form.value.email.includes('@') &&
         form.value.password.length >= 8 &&
         form.value.password === form.value.confirmPassword
})

// Methods
const validateForm = () => {
  errors.value = {}
  
  if (!form.value.firstName) {
    errors.value.firstName = 'First name is required'
  }
  
  if (!form.value.lastName) {
    errors.value.lastName = 'Last name is required'
  }
  
  if (!form.value.username) {
    errors.value.username = 'Username is required'
  } else if (form.value.username.length < 3) {
    errors.value.username = 'Username must be at least 3 characters'
  }
  
  if (!form.value.email) {
    errors.value.email = 'Email is required'
  } else if (!form.value.email.includes('@')) {
    errors.value.email = 'Please enter a valid email address'
  }
  
  if (!form.value.password) {
    errors.value.password = 'Password is required'
  } else if (form.value.password.length < 8) {
    errors.value.password = 'Password must be at least 8 characters'
  }
  
  if (!form.value.confirmPassword) {
    errors.value.confirmPassword = 'Please confirm your password'
  } else if (form.value.password !== form.value.confirmPassword) {
    errors.value.confirmPassword = 'Passwords do not match'
  }
  
  if (!form.value.agreeToTerms) {
    errors.value.agreeToTerms = 'You must agree to the terms and conditions'
  }
  
  return Object.keys(errors.value).length === 0
}

const handleRegister = async () => {
  // Clear previous errors
  authStore.clearError()
  
  if (!validateForm()) {
    return
  }

  const result = await authStore.register({
    name: `${form.value.firstName} ${form.value.lastName}`.trim(),
    email: form.value.email,
    password: form.value.password
  })

  if (result.success) {
    toastManager.success(
      'Account Created!',
      'Please check your email and click the verification link. Then sign in below.',
      { duration: 8000 }
    )

    // Switch to login form — user must verify email before they can sign in
    emit('switch-mode', 'login')
  }
}

onMounted(() => {
  // Focus first name field
  const firstNameInput = document.getElementById('firstName')
  if (firstNameInput) {
    firstNameInput.focus()
  }
})
</script>